import { Mui, MuiIcons } from "@osu/react-ui";

const FORCED_AUTHENTICATION = process.env.REACT_APP_FORCED_AUTHENTICATION === "true"
const OSU_NAV_ID = 'osu-navbar'
const MAIN_ID = 'page-content'
const ACCESSIBLE_REFLOW_HEIGHT = `399px`
const TOC_WIDTH = `15rem`
const NAVBAR_WRAPPER_ID = "app-header"
const APPLICATION_NAME = (process.env.APPLICATION_NAME) || "Honors & Scholars";

const DARK_MODE_CLASSNAME = 'dark-mode-enabled'

// Dark mode constants
const THEME_CACHE_KEYS = {
  darkMode: "darkMode",
  system: "system",
  dark: "dark",
  light: "light"
};

const CONFIG_VIEW = {
  RUBRIC_VIEW: 'RUBRIC_VIEW',
  CRITERIA_VIEW: 'CRITERIA_VIEW',
}

const ATTRIBUTE_WHITELIST = [
  'bestTest',
  'classRankPercentile',
]

const CACHE_OPTIONS = {
  [THEME_CACHE_KEYS.darkMode]: {
    [THEME_CACHE_KEYS.system]: "Use system settings",
    [THEME_CACHE_KEYS.dark]: "Dark",
    [THEME_CACHE_KEYS.light]: "Light"
  }
};

const TOOLTIP_STATUS = {
  ASSIGNED: "ASSIGNED",
  PRIORITY: "PRIORITY",
  RESUMELATER: "RESUMELATER",
  TRAINING: "TRAINING",
  PASTDUE: "PASTDUE",
  HOLD_FOR_ADMIN: "HOLD_FOR_ADMIN",
  CURRENTLY_VIEWING: "CURRENTLY_VIEWING",
  APPEAL_REQUESTED: "APPEAL_REQUESTED",
  PROGRAM_CHANGE_REQUESTED: "PROGRAM_CHANGE_REQUESTED",
  WITHDRAW_REQUESTED: "WITHDRAW_REQUESTED",
  PENDING_PROCESSING: "PENDING_PROCESSING",
  STUDENT_REQUEST_APPROVED: "STUDENT_REQUEST_APPROVED",
  ADMIN_RECOMMENDATION: "ADMIN_RECOMMENDATION",
}

const SUCCESS_API_ENDPOINT_WHITELIST = {
  ARCHIVE_RUBRIC: "archiveRubric",
  CLONE_RUBRIC: "cloneRubric",
  CREATE_RUBRIC: "createARubric",
  DELETE_RUBRIC: "deleteRubric",
  SAVE_RUBRIC: "saveRubricV2",
  SET_ACTIVE_RUBRIC: "setActiveRubric",
  SAVE_CRITERIA: "saveCriteria",
  UPDATE_REVIEWER: "updateReviewer",
}

const ERROR_API_ENDPOINT_BLACKLIST = {
  APPLICATION_RESTART_REVIEW: "applicationRestartReview",
  APPLICATION_ASSIGN_REVIEW: "applicationAssignReview",
  APPLICATION_REASSIGN_REVIEW: "applicationReassignReview",
  APPLICATION_UNASSIGN_REVIEW: "applicationUnassignReview",
  APPLICATION_SUBMIT_REVIEW: "applicationSubmitReview",
}

const ACTION_TYPES = {
  QUERY_FULFILLED: 'graphqlApi/executeQuery/fulfilled',
  QUERY_REJECTED: 'graphqlApi/executeQuery/rejected',
  MUTATION_FULFILLED: 'graphqlApi/executeMutation/fulfilled',
  MUTATION_REJECTED: 'graphqlApi/executeMutation/rejected',
};

const STATUSES = {
  IDLE: "idle",
  LOADING: "loading",
  ERROR: "error",
  SUCCESS: "success",
  CLEARED: "cleared",
  UNAUTH: "unauth",
  WARNING: "warning",
  COGNITO_FAILURE: "COGNITO_FAILURE",
}

const TOOLTIPS = (type, data) => {
  const iconStyles = {
    margin: '0rem 0 .1rem .3rem'
  }

  switch (type) {
    case TOOLTIP_STATUS.ASSIGNED:
      return (
        <Mui.Tooltip title={"This application has been manually assigned."} placement="right">
          <MuiIcons.AssignmentLate fontSize="small" style={{ color: '#ffbe45', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.PRIORITY:
      return (
        <Mui.Tooltip title={"This application has been marked as a priority."} placement="right">
          <MuiIcons.AssignmentLate fontSize="small" color='primary' style={{ ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.RESUMELATER:
      return (
        <Mui.Tooltip title={"This application has been saved for later."} placement="right">
          <MuiIcons.Bookmark fontSize="small" style={{ color: '#639ed6', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.TRAINING:
      return (
        <Mui.Tooltip title={"This application has been cloned for training."} placement="right">
          <MuiIcons.School fontSize="small" style={{ color: '#ffbe45', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.PASTDUE:
      return (
        <Mui.Tooltip title={"This application is past due."} placement="right">
          <MuiIcons.AccessAlarm fontSize="small" style={{ color: '#f72d05', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.HOLD_FOR_ADMIN:
      return (
        <Mui.Tooltip title={`Held since ${data}`} placement="right">
          <MuiIcons.Policy fontSize="small" style={{ color: '#a8224a', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.CURRENTLY_VIEWING:
      return (
        <Mui.Tooltip title={"Currently Viewing"} placement="top">
          <MuiIcons.Circle className={'margin-left-1'} color='primary' sx={{ fontSize: 10 }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.APPEAL_REQUESTED:
      return (
        <Mui.Tooltip title={"Appeal Requested"} placement="right">
          <MuiIcons.Adjust fontSize="small" style={{ color: '#0621d0ff', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.PROGRAM_CHANGE_REQUESTED:
      return (
        <Mui.Tooltip title={"Program Change Requested"} placement="right">
          <MuiIcons.Explore fontSize="small" style={{ color: '#0a5c02ff', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.WITHDRAW_REQUESTED:
      return (
        <Mui.Tooltip title={"Withdraw Requested"} placement="right">
          <MuiIcons.Cancel fontSize="small" style={{ color: '#6d1004ff', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.PENDING_PROCESSING:
      return (
        <Mui.Tooltip title={"Pending Processing"} placement="right">
          <MuiIcons.HourglassEmpty fontSize="small" style={{ color: '#2ba1efff', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.STUDENT_REQUEST_APPROVED:
      return (
        <Mui.Tooltip title={"Student Request Approved"} placement="right">
          <MuiIcons.CheckCircle fontSize="small" style={{ color: '#4caf50', ...iconStyles }} />
        </Mui.Tooltip>
      )
    case TOOLTIP_STATUS.ADMIN_RECOMMENDATION:
      return (
        <Mui.Tooltip title={"Admin Recommendation"} placement="top">
          <MuiIcons.PersonPin className={'margin-left-1'} color='primary' sx={{ fontSize: 20 }} />
        </Mui.Tooltip>
      )

    default:
      return null
  }
}

const PROGRAMS = {
  HONORS: { KEY: "honors", label: 'Honors' },
  SCHOLARS: { KEY: "scholars", label: 'Scholars' },
}

const REVIEW_TYPES = {
  PADM: { KEY: 'PADM', LABEL: 'Presumptive Admit' },
  PDNY: { KEY: 'PDNY', LABEL: 'Presumptive Deny' },
  HON1: { KEY: 'HON1', LABEL: 'Single Read' },
  HON2: { KEY: 'HON2', LABEL: 'Double Read' },
  // temporary scholars code
  SCH1: { KEY: 'SCH1', LABEL: 'Single Read' },
}

const PROGRAM_COLOR_MAP = {
  "honors": "#00CCFF",
  "scholars": "#CCCCFF",
  // "domestic-transfer": "#99CC66",
  // "international-transfer": "#FFCCFF",
  // "military": "#FFCCCC",
  // "non-traditional": "#FFCC00"
};


const DASHBOARD_BUTTON_GROUP = {
  RESUMELATER: { KEY: "RESUMELATER", LABEL: 'Saved', TYPE: 'TOGGLE_RESUMELATER' },
  ASSIGNED: { KEY: "ASSIGNED", LABEL: 'Assigned', TYPE: 'TOGGLE_ASSIGNED' },
};

const AUTH_ROLES = {
  // Generic
  ANY_ADMIN: 'ANY_ADMIN',
  ADMIN_ACCESS: "ADMIN-ACCESS",
  READ_ACCESS: "READ-ACCESS",

  // Program-specific
  HONORS_ADMIN: "HONORS-ADMIN",
  HONORS_REVIEWER: "HONORS-REVIEWER",
  SCHOLARS_ADMIN: "SCHOLARS-ADMIN",
  SCHOLARS_REVIEWER: "SCHOLARS-REVIEWER",
  STUDENT_EMPLOYEE: "STUDENT-EMPLOYEE",
  HONORS_SEASONAL_MANAGER: "HONORS-SEASONAL-MANAGER",
  HONORS_PROGRAM_COORDINATOR: "HONORS-PROGRAM-COORDINATOR",

  //   // TODO Delete:
  //   ADMIN: "ADMIN",
  //   READER: "READER", // todo: remove reader
  //   REVIEWER: "REVIEWER",
  //   UNAUTHORIZED: "UNAUTHORIZED",
  //   ADMIN_REVIEWER: "ADMIN_REVIEWER"
}

const AUTH_ROLES_CHECK = {
  IS_HONORS_ADMIN: 'isHonorsAdmin',
  IS_HONORS_REVIEWER: 'isHonorsReviewer',
  IS_SCHOLARS_ADMIN: 'isScholarsAdmin',
  IS_SCHOLARS_REVIEWER: 'isScholarsReviewer',
  IS_ANY_ADMIN: 'isAnyAdmin',
  IS_ANY_REVIEWER: 'isAnyReviewer',
  IS_STUDENT: 'isStudent',
  IS_HONORS_SEASONAL_MANAGER: 'isHonorsSeasonalManager',
  IS_HONORS_PROGRAM_COORDINATOR: 'isHonorsProgramCoordinator',
}

const STUDENT_REQUEST_ACTIONS = {
  APPEAL: { KEY: "APPEAL", label: "Appeal" },
  PROGRAM_CHANGE: { KEY: "PROGRAMCHANGE", label: "Program Change" },
  WITHDRAW: { KEY: "WITHDRAW", label: "Withdraw" }
};

const STATUS_BOOLEAN = {
  YES: 'Yes',
  NO: 'No',
}

const SEVERITY_LEVELS = {
  INFO: 'INFO',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
}

const ERROR_CONSTS = {
  UI: {
    GENERIC: {
      EMPTY_ALERT: {
        MESSAGE: '',
        SEVERITY: null,
      },
    },
  },
};

const ALERT_MESSAGES = {
  ERROR_GENERIC:
    'Error retreiving data.',
};

const RECOMMENDATIONS = {
  ADMIT: "ADMIT", // Only need to validate against this for now (AFAIK)
};

const RUBRIC_STATUS = {
  ARCHIVED: "ARCHIVED",
  ACTIVE: "ACTIVE",
  STAGED: "STAGED",
}

const APPLICATION_STATUS = {
  RESUME_LATER: 'RESUMELATER',
  ADMINREVIEW: 'ADMINREVIEW',
  PENDINGAPPROVAL: 'PENDINGAPPROVAL',
  CANCELLED: 'CANCELLED',
  COMPLETE: 'COMPLETE',
}

const API_STATUS = {
  LOADING: "LOADING",
  ERROR: "ERROR",
  SUCCESS: "SUCCESS"
}

const AUTH_STATUS = {
  CLEARED: "cleared",
  COGNITO_FAILURE: "COGNITO_FAILURE",
  IDLE: "idle",
  LOADING: "loading",
}

const DOCUMENTTYPE_TO_KEYWORDNAMES = {
  "ADM Application": [],
  "ADM High School Document": ["High School Document Type"],
  "ADM Correspondence": ["Correspondence Type"],
  "ADM University Document": ["University Document Type"],
};

const REPORTS_LABELS = {
  notstarted: { KEY: "notstarted", LABEL: "Not Started" },
  inreview: { KEY: "inreview", LABEL: "In Review" },
  pastdue: { KEY: "pastdue", LABEL: "Past Due" },
  recommendations: { KEY: "recommendations", LABEL: "Recommendations to Approve" },
  publish: { KEY: "publish", LABEL: "Publish" },
  complete: { KEY: "complete", LABEL: "Completed" },
  appeal: { KEY: "appeal", LABEL: "Appeal Requested" },
  changeOrWithdraw: { KEY: "changeOrWithdraw", LABEL: "Program Change or Withdraw" },
  funnel:       {KEY: "funnel", LABEL: "Funnel" },
  reviewTypeChange: {KEY: "reviewTypeChange", LABEL: "Review Type Change By System Criteria" },
  cancelled:    {KEY: "cancelled", LABEL: "Cancelled" },
  adminnotes:    {KEY: "adminnotes", LABEL: "Admin Notes" },
  conflictOfInterest: { KEY: "conflictOfInterest", LABEL: "Conflict of Interest" },
};

const OPERATORS = {
  GT: 'GT',
  LT: 'LT',
  GTE: 'GTE',
  LTE: 'LTE',
  NE: 'NE',
  EQ: 'EQ',
}

const OPERATORS_OBJ = [
  { KEY: 'isGreaterThan', OPERATOR: '>', LABEL: 'Greater Than' },
  { KEY: 'isLessThan', OPERATOR: '<', LABEL: 'Less Than' },
  { KEY: 'isGreaterThanEqualTo', OPERATOR: '>=', LABEL: 'Greater Than or Equal To' },
  { KEY: 'isLessThanEqualTo', OPERATOR: '<=', LABEL: 'Less Than or Equal To' },
  { KEY: 'notEquals', OPERATOR: '<>', LABEL: 'Not Equal To' },
  { KEY: 'equals', OPERATOR: '=', LABEL: 'Equal To' },
  { KEY: 'BT', OPERATOR: 'BETWEEN', LABEL: 'Between' },
];

const NUMBER_OPERATORS = [
  { KEY: 'GT', LABEL: 'Greater Than' },
  { KEY: 'LT', LABEL: 'Less Than' },
  { KEY: 'GTE', LABEL: 'Greater Than or Equal To' },
  { KEY: 'LTE', LABEL: 'Less Than or Equal To' },
  { KEY: 'NE', LABEL: 'Not Equal To' },
  { KEY: 'EQ', LABEL: 'Equal To' },
  { KEY: 'BT', LABEL: 'Between' },
  { KEY: 'NOTNULL', LABEL: 'Not Null' },
  { KEY: 'ISNULL', LABEL: 'Is Null' }
];

const SEARCH_OPERATORS = {
  text: [
    { KEY: 'CNT', LABEL: 'Contains' },
    { KEY: 'EQ', LABEL: 'Equal To' },
    { KEY: 'NE', LABEL: 'Not Equal To' },
    { KEY: 'NOTNULL', LABEL: 'Not Null' },
    { KEY: 'ISNULL', LABEL: 'Is Null' }
  ],
  number: NUMBER_OPERATORS,
  float: NUMBER_OPERATORS,
  long: NUMBER_OPERATORS,
  integer: NUMBER_OPERATORS,
  date: [
    { KEY: 'GT', LABEL: 'After' },
    { KEY: 'LT', LABEL: 'Before' },
    { KEY: 'BT', LABEL: 'Between' },
    { KEY: 'NOTNULL', LABEL: 'Not Null' },
    { KEY: 'ISNULL', LABEL: 'Is Null' }
  ]
};


export {
  ACCESSIBLE_REFLOW_HEIGHT, ALERT_MESSAGES, API_STATUS, APPLICATION_NAME, APPLICATION_STATUS,
  AUTH_STATUS, AUTH_ROLES,
  PROGRAM_COLOR_MAP, DASHBOARD_BUTTON_GROUP, PROGRAMS, REVIEW_TYPES, ATTRIBUTE_WHITELIST,
  DARK_MODE_CLASSNAME, THEME_CACHE_KEYS, CACHE_OPTIONS,
  ERROR_CONSTS, FORCED_AUTHENTICATION, MAIN_ID, NAVBAR_WRAPPER_ID,
  OSU_NAV_ID, RECOMMENDATIONS,
  SEVERITY_LEVELS,
  STATUS_BOOLEAN, TOOLTIP_STATUS, TOC_WIDTH, RUBRIC_STATUS,
  TOOLTIPS,
  DOCUMENTTYPE_TO_KEYWORDNAMES,
  REPORTS_LABELS,
  SUCCESS_API_ENDPOINT_WHITELIST, ERROR_API_ENDPOINT_BLACKLIST,
  STATUSES, ACTION_TYPES, CONFIG_VIEW,
  OPERATORS, OPERATORS_OBJ,
  SEARCH_OPERATORS, STUDENT_REQUEST_ACTIONS, AUTH_ROLES_CHECK
};